import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { CarpoolVehicle } from "../types";
import baserowApi from "../services/baserowApi";
import { Users, Car, UserPlus, X, Unlink, Upload, Clock, CheckCircle, History, FileText, Calendar, Download } from "lucide-react";

// Hilfsfunktion: Berechne Ablaufdatum (Kontrolldatum + 6 Monate)
const calculateExpiryDate = (checkDate: string): string => {
  const date = new Date(checkDate);
  date.setMonth(date.getMonth() + 6);
  return date.toISOString().split('T')[0]; // YYYY-MM-DD Format
};

// Hilfsfunktion: Formatiere Datum für Anzeige
const formatDateForDisplay = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE'); // DD.MM.YYYY Format
};

interface UserAssignment {
  id: number;
  name: string;
  role: string;
  licenseCheck: string;
  assignedVehicles: Array<{
    licensePlate: string;
    model: string;
    taxation: string;
  }>;
  isPoolCar?: boolean;
  avatarInitials?: string;
}

interface AvailableVehicle {
  id: number;
  licensePlate: string;
  model: string;
  taxation: string;
  manufacturer: string;
}

interface LicenseCheck {
  id: number;
  user_id: number;
  check_date: string;
  is_checked: boolean;
  uploaded_file?: {
    name: string;
    url: string;
  };
  notes?: string;
  created_at: string;
}

// LicenseCheckModal Komponente
const LicenseCheckModal = ({ open, user, onClose, onLicenseCheck }: { 
  open: boolean; 
  user: UserAssignment; 
  onClose: () => void;
  onLicenseCheck: (userId: number, checkData: any) => void;
}) => {
  const [isChecked, setIsChecked] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [checkDate, setCheckDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isChecked) {
      alert('Bitte bestätigen Sie die Führerscheinkontrolle.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Echte Baserow-API-Integration
      const checkData = {
        title: `Führerscheinkontrolle ${user.name}`,
        user_id: user.id, // Verwende die echte Benutzer-ID
        check_date: checkDate,
        is_checked: isChecked,
        uploaded_file: uploadedFile || undefined,
        notes: `Führerscheinkontrolle für ${user.name}`
      };
      
      const result = await baserowApi.createLicenseCheck(checkData);
      
      console.log('Führerscheinkontrolle erfolgreich erstellt:', result);
      
      // Zeige Erfolgsmeldung
      alert(`✅ Führerscheinkontrolle für ${user.name} erfolgreich gespeichert!`);
      
      // Aktualisiere den lokalen State
      onLicenseCheck(user.id, {
        checkDate,
        isChecked,
        uploadedFile
      });
      
      onClose();
    } catch (error: any) {
      console.error('Fehler bei der Führerscheinkontrolle:', error);
      alert(`Fehler bei der Führerscheinkontrolle: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-40 pointer-events-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md relative animate-fade-in">
        <button 
          type="button" 
          onClick={onClose} 
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl"
        >
          &times;
        </button>
        
        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
          Führerscheinkontrolle
        </h3>
        
        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Benutzer: <strong>{user.name}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Checkbox für Kontrolle */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="licenseChecked"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="licenseChecked" className="text-gray-700 dark:text-gray-200 font-medium">
              Führerschein kontrolliert
            </label>
          </div>

          {/* Datum der Kontrolle */}
          <div>
            <label className="block text-gray-700 dark:text-gray-200 mb-2 font-medium">
              Datum der Kontrolle
            </label>
            <input
              type="date"
              value={checkDate}
              onChange={(e) => setCheckDate(e.target.value)}
              className="border rounded-lg p-3 w-full dark:bg-gray-900 dark:text-gray-100"
              required
            />
          </div>

          {/* Datei-Upload */}
          <div>
            <label className="block text-gray-700 dark:text-gray-200 mb-2 font-medium">
              Führerschein hochladen
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <input
                type="file"
                onChange={handleFileUpload}
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                id="licenseFile"
              />
              <label htmlFor="licenseFile" className="cursor-pointer text-blue-600 hover:text-blue-700">
                Datei auswählen
              </label>
              {uploadedFile && (
                <div className="mt-2 flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {uploadedFile.name}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Info über 6-Monats-Speicherung */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Speicherung für 6 Monate
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                  Der Führerschein wird 6 Monate gespeichert und dann automatisch erneuert.
                </p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 py-3 px-4 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={!isChecked || isSubmitting}
              className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-semibold"
            >
              {isSubmitting ? 'Speichern...' : 'Kontrolle speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

// LicenseHistoryModal Komponente
const LicenseHistoryModal = ({ open, user, onClose }: { 
  open: boolean; 
  user: UserAssignment; 
  onClose: () => void;
}) => {
  const [licenseChecks, setLicenseChecks] = useState<LicenseCheck[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadLicenseHistory();
    }
  }, [open, user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLicenseHistory = async () => {
    setLoading(true);
    try {
      // Echte Baserow-API-Integration - lade alle Kontrollen
      const response = await baserowApi.getLicenseChecks();
      
      console.log('Geladene Führerscheinkontrollen:', response.results);
      
      // Client-seitige Filterung nach Benutzer-ID
      const userChecks = response.results.filter((item: any) => {
        // Prüfe, ob die Kontrolle dem aktuellen Benutzer gehört
        const checkUserId = item.user_id || item.user_name?.split(' ')[1] || 0;
        const matches = checkUserId === user.id || item.user_name?.includes(user.name);
        console.log(`Kontrolle ${item.id}: user_id=${item.user_id}, user_name=${item.user_name}, matches=${matches}`);
        return matches;
      });
      
      console.log('Gefilterte Kontrollen für Benutzer:', userChecks);
      
      // Transformiere die API-Antwort in unser Format
      const checks: LicenseCheck[] = userChecks.map((item: any) => {
        const check = {
          id: item.id,
          user_id: item.user_id,
          check_date: item.check_date,
          is_checked: item.is_checked,
          uploaded_file: item.uploaded_file ? {
            name: item.uploaded_file.name || 'Dokument',
            url: item.uploaded_file.url || '#'
          } : undefined,
          notes: item.notes,
          created_at: item.created_at
        };
        
        console.log(`Transformierte Kontrolle ${check.id}:`, check);
        return check;
      });
      
      // Sortiere nach Kontrolldatum - neueste zuerst
      checks.sort((a, b) => new Date(b.check_date).getTime() - new Date(a.check_date).getTime());
      
      setLicenseChecks(checks);
    } catch (error: any) {
      console.error('Fehler beim Laden der Führerschein-Historie:', error);
      alert(`Fehler beim Laden der Historie: ${error.message || 'Unbekannter Fehler'}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  const isExpired = (checkDate: string) => {
    const checkDateObj = new Date(checkDate);
    const sixMonthsLater = new Date(checkDateObj);
    sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
    return new Date() > sixMonthsLater;
  };

  const getDaysUntilExpiry = (checkDate: string) => {
    const checkDateObj = new Date(checkDate);
    const sixMonthsLater = new Date(checkDateObj);
    sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
    const now = new Date();
    const diffTime = sixMonthsLater.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50 pointer-events-auto backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-4xl max-h-[85vh] overflow-y-auto relative animate-fade-in border border-gray-200">
        <button 
          type="button" 
          onClick={onClose} 
          className="absolute top-6 right-6 p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all duration-200"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <History className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">
                Führerschein-Historie
              </h3>
              <p className="text-lg text-gray-600">
                <strong>{user.name}</strong>
              </p>
            </div>
          </div>
          
          {/* Statistik-Banner */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-blue-600">{licenseChecks.length}</p>
                <p className="text-sm text-gray-600">Kontrollen gesamt</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {licenseChecks.filter(check => !isExpired(check.check_date)).length}
                </p>
                <p className="text-sm text-gray-600">Gültige Kontrollen</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">
                  {licenseChecks.filter(check => isExpired(check.check_date)).length}
                </p>
                <p className="text-sm text-gray-600">Abgelaufene Kontrollen</p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 dark:text-gray-400 mt-2">Lade Historie...</p>
          </div>
        ) : licenseChecks.length === 0 ? (
          <div className="text-center py-8">
            <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Keine Führerscheinkontrollen gefunden.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {licenseChecks.map((check, index) => {
              const expired = isExpired(check.check_date);
              const daysUntilExpiry = getDaysUntilExpiry(check.check_date);
              const expiryDate = new Date(check.check_date);
              expiryDate.setMonth(expiryDate.getMonth() + 6);
              
              return (
                <div key={check.id} className={`relative overflow-hidden rounded-xl border-2 transition-all duration-200 hover:shadow-lg ${
                  expired 
                    ? 'border-red-200 bg-gradient-to-r from-red-50 to-red-100' 
                    : daysUntilExpiry <= 30 
                    ? 'border-yellow-200 bg-gradient-to-r from-yellow-50 to-yellow-100'
                    : 'border-green-200 bg-gradient-to-r from-green-50 to-green-100'
                }`}>
                  {/* Status-Indikator */}
                  <div className={`absolute top-0 left-0 w-1 h-full ${
                    expired ? 'bg-red-500' : daysUntilExpiry <= 30 ? 'bg-yellow-500' : 'bg-green-500'
                  }`}></div>
                  
                  <div className="p-5 pl-8">
                    {/* Header mit Datum und Status */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-full ${
                          expired ? 'bg-red-200' : daysUntilExpiry <= 30 ? 'bg-yellow-200' : 'bg-green-200'
                        }`}>
                          <Calendar className={`w-5 h-5 ${
                            expired ? 'text-red-600' : daysUntilExpiry <= 30 ? 'text-yellow-600' : 'text-green-600'
                          }`} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 text-lg">
                            Kontrolle #{index + 1}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {formatDate(check.check_date)}
                          </p>
                        </div>
                        {check.is_checked && (
                          <div className="flex items-center space-x-1 bg-green-100 px-2 py-1 rounded-full">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-xs font-medium text-green-700">Kontrolliert</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Status Badge */}
                      <div className={`px-4 py-2 rounded-full font-medium text-sm shadow-sm ${
                        expired 
                          ? 'bg-red-500 text-white' 
                          : daysUntilExpiry <= 30 
                          ? 'bg-yellow-500 text-white'
                          : 'bg-green-500 text-white'
                      }`}>
                        {expired ? 'Abgelaufen' : daysUntilExpiry <= 30 ? 'Läuft bald ab' : 'Gültig'}
                      </div>
                    </div>

                    {/* Gültigkeitsinformationen */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-white/70 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center space-x-2 mb-1">
                          <Clock className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium text-gray-700">Gültig bis</span>
                        </div>
                        <p className="text-lg font-semibold text-gray-900">
                          {formatDate(expiryDate.toISOString().split('T')[0])}
                        </p>
                        <p className="text-xs text-gray-500">
                          {expired ? 'Abgelaufen' : `Noch ${daysUntilExpiry} Tage gültig`}
                        </p>
                      </div>
                      
                      {check.uploaded_file && (
                        <div className="bg-white/70 rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center space-x-2 mb-2">
                            <FileText className="w-4 h-4 text-blue-500" />
                            <span className="text-sm font-medium text-gray-700">Führerscheinbild</span>
                          </div>
                          <button 
                            onClick={() => {
                              console.log('Button geklickt für Dokument:', check.uploaded_file);
                              
                              if (check.uploaded_file?.url && check.uploaded_file.url !== '#') {
                                console.log('Versuche Dokument zu öffnen:', check.uploaded_file.url);
                                
                                if (check.uploaded_file.url.startsWith('blob:')) {
                                  try {
                                    const newWindow = window.open(check.uploaded_file.url, '_blank');
                                    if (!newWindow) {
                                      const link = document.createElement('a');
                                      link.href = check.uploaded_file.url;
                                      link.download = check.uploaded_file.name || 'dokument';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }
                                  } catch (error) {
                                    console.error('Fehler beim Öffnen des Blob-Dokuments:', error);
                                    alert('Fehler beim Öffnen des Dokuments. Bitte versuchen Sie es erneut.');
                                  }
                                } else {
                                  try {
                                    const newWindow = window.open(check.uploaded_file.url, '_blank');
                                    if (!newWindow) {
                                      const link = document.createElement('a');
                                      link.href = check.uploaded_file.url;
                                      link.target = '_blank';
                                      link.download = check.uploaded_file.name || 'dokument';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }
                                  } catch (error) {
                                    console.error('Fehler beim Öffnen des Dokuments:', error);
                                    alert('Fehler beim Öffnen des Dokuments. Bitte versuchen Sie es erneut.');
                                  }
                                }
                              } else {
                                console.warn('Dokument-URL nicht verfügbar oder ungültig:', check.uploaded_file);
                                alert('Dokument-URL nicht verfügbar. Das Dokument wurde möglicherweise nicht korrekt hochgeladen.');
                              }
                            }}
                            className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                              check.uploaded_file?.url && check.uploaded_file.url !== '#' 
                                ? 'bg-blue-500 text-white hover:bg-blue-600 hover:shadow-md transform hover:-translate-y-0.5' 
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                            disabled={!check.uploaded_file?.url || check.uploaded_file.url === '#'}
                          >
                            <Download className="w-4 h-4" />
                            <span>Bild anzeigen</span>
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Notizen */}
                    {check.notes && (
                      <div className="bg-white/70 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center space-x-2 mb-2">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Notizen</span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {check.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-blue-100 rounded-full">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-base font-semibold text-blue-800 mb-1">
                  Automatische Gültigkeitsprüfung
                </p>
                <p className="text-sm text-blue-600 leading-relaxed">
                  Führerscheinkontrollen sind 6 Monate gültig. Das System berechnet automatisch das Ablaufdatum und zeigt den aktuellen Status an.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-8 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default function FahrerNutzerTable() {
  const [users, setUsers] = useState<UserAssignment[]>([]);
  const [availableVehicles, setAvailableVehicles] = useState<AvailableVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserAssignment | null>(null);
  const [assigningVehicle, setAssigningVehicle] = useState<AvailableVehicle | null>(null);
  const [showLicenseCheckModal, setShowLicenseCheckModal] = useState(false);
  const [selectedUserForLicenseCheck, setSelectedUserForLicenseCheck] = useState<UserAssignment | null>(null);
  const [showLicenseHistoryModal, setShowLicenseHistoryModal] = useState(false);
  const [selectedUserForLicenseHistory, setSelectedUserForLicenseHistory] = useState<UserAssignment | null>(null);

  // Funktion: Lade letzte Führerscheinkontrolle für einen Benutzer
  const getLatestLicenseCheck = async (userId: number): Promise<string> => {
    try {
      const response = await baserowApi.getLicenseChecks();
      const userChecks = response.results.filter((item: any) => {
        const checkUserId = item.user_id || item.user_name?.split(' ')[1] || 0;
        return checkUserId === userId || item.user_name?.includes(userId.toString());
      });
      
      if (userChecks.length === 0) {
        return '—'; // Keine Kontrollen gefunden
      }
      
      // Sortiere nach Datum (neueste zuerst)
      userChecks.sort((a: any, b: any) => new Date(b.check_date).getTime() - new Date(a.check_date).getTime());
      
      const latestCheck = userChecks[0];
      const expiryDate = calculateExpiryDate(latestCheck.check_date);
      return formatDateForDisplay(expiryDate);
    } catch (error) {
      console.error(`Fehler beim Laden der Führerscheinkontrolle für Benutzer ${userId}:`, error);
      return '—';
    }
  };



  useEffect(() => {
    loadUserAssignments();
  }, []);

  const loadUserAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Lade alle Fahrzeuge und Benutzer parallel
      const [vehiclesResponse, usersResponse] = await Promise.all([
        baserowApi.getVehicles(),
        baserowApi.getUsers()
      ]);
      
      const vehicles: CarpoolVehicle[] = vehiclesResponse.results;
      const allUsers = usersResponse.results;
      
      // Erstelle eine Map aller Benutzer für schnelle Suche
      const usersById = new Map();
      allUsers.forEach((user: any) => {
        usersById.set(user.id, user);
      });
      
      // Extrahiere eindeutige Benutzer aus den Fahrzeugzuordnungen
      const userMap = new Map<string, UserAssignment>();
      const assignedVehicleIds = new Set<number>();
      
      vehicles.forEach(vehicle => {
        if (vehicle.user && Array.isArray(vehicle.user) && vehicle.user.length > 0) {
          // Fahrzeug ist einem Benutzer zugewiesen
          vehicle.user.forEach(user => {
            const userId = user.id;
            const userInitials = user.value; // Die Abkürzung (bg, fp, kp)
            
            // Hole die vollständigen Benutzerdaten aus der users Tabelle
            const fullUserData = usersById.get(userId);
            const fullName = fullUserData ? 
              `${fullUserData.first_name || ''} ${fullUserData.last_name || ''}`.trim() : 
              fullUserData?.name || userInitials;
            
            // Zeige sowohl den vollständigen Namen als auch die Abkürzung
            const displayName = fullName && fullName !== userInitials ? 
              `${fullName} (${userInitials})` : 
              userInitials;
            
            // Erstelle Initialen für Avatar basierend auf dem vollständigen Namen
            let avatarInitials = userInitials; // Fallback auf Abkürzung
            if (fullUserData && (fullUserData.first_name || fullUserData.last_name)) {
              const firstName = fullUserData.first_name || '';
              const lastName = fullUserData.last_name || '';
              avatarInitials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
            } else if (fullUserData?.name) {
              // Falls nur ein "name" Feld existiert, nutze die ersten zwei Buchstaben
              const nameParts = fullUserData.name.split(' ');
              if (nameParts.length >= 2) {
                avatarInitials = `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
              } else {
                avatarInitials = fullUserData.name.substring(0, 2).toUpperCase();
              }
            }
            
            if (!userMap.has(userId.toString())) {
              userMap.set(userId.toString(), {
                id: userId,
                name: displayName,
                role: fullUserData?.role || 'Fahrer',
                licenseCheck: '—', // Wird später durch echte Daten ersetzt
                assignedVehicles: [],
                avatarInitials: avatarInitials
              });
            }
            
            // Füge das Fahrzeug zur Benutzerzuordnung hinzu
            const userAssignment = userMap.get(userId.toString())!;
            userAssignment.assignedVehicles.push({
              licensePlate: vehicle.license_plate || 'Kein Kennzeichen',
              model: vehicle.model,
              taxation: vehicle.taxation || 'Keine Versteuerung'
            });
            assignedVehicleIds.add(vehicle.id);
          });
        }
      });
      
      // Erstelle Poolfahrzeuge nur für NICHT zugewiesene Fahrzeuge
      vehicles.forEach(vehicle => {
        if (!assignedVehicleIds.has(vehicle.id)) {
          // Fahrzeug ist NICHT zugewiesen - nur dann als Poolfahrzeug anzeigen
          const poolCar: UserAssignment = {
            id: vehicle.id,
            name: 'Poolfahrzeug',
            role: 'Verfügbar',
            licenseCheck: '—',
            assignedVehicles: [{
              licensePlate: vehicle.license_plate || 'Kein Kennzeichen',
              model: vehicle.model,
              taxation: vehicle.taxation || 'Keine Versteuerung'
            }],
            isPoolCar: true
          };
          userMap.set(`pool_${vehicle.id}`, poolCar);
        }
      });
      
      // Erstelle Liste verfügbarer Fahrzeuge (nur die, die wirklich nicht zugewiesen sind)
      const available = vehicles
        .filter(vehicle => !assignedVehicleIds.has(vehicle.id))
        .map(vehicle => {
          return {
            id: vehicle.id,
            licensePlate: vehicle.license_plate || 'Kein Kennzeichen',
            model: vehicle.model,
            taxation: vehicle.taxation || 'Keine Versteuerung',
            manufacturer: typeof vehicle.manufacturer === 'string' 
              ? vehicle.manufacturer 
              : vehicle.manufacturer?.value || 'Unbekannt'
          };
        });
      
      setAvailableVehicles(available);
      
      // Konvertiere Map zu Array
      const userAssignments = Array.from(userMap.values());
      
      // Sortiere: Zuerst Benutzer, dann Poolfahrzeuge
      userAssignments.sort((a, b) => {
        if (a.isPoolCar && !b.isPoolCar) return 1;
        if (!a.isPoolCar && b.isPoolCar) return -1;
        return a.name.localeCompare(b.name);
      });
      
      setUsers(userAssignments);
      
      // Lade Führerschein-Ablaufdaten für alle echten Benutzer (nicht Poolfahrzeuge)
      const realUsers = userAssignments.filter(user => !user.isPoolCar);
      for (const user of realUsers) {
        getLatestLicenseCheck(user.id).then(expiryDate => {
          setUsers(prevUsers => prevUsers.map(u => 
            u.id === user.id ? { ...u, licenseCheck: expiryDate } : u
          ));
        });
      }
    } catch (err) {
      console.error('Fehler beim Laden der Benutzerzuordnungen:', err);
      setError('Fehler beim Laden der Benutzerdaten aus Baserow.');
      
      // Fallback zu Dummy-Daten bei Fehler
      setUsers([
        { id: 1, name: "Max Mustermann", role: "Fahrer", licenseCheck: "01.06.2025", assignedVehicles: [] },
        { id: 2, name: "Erika Musterfrau", role: "Nutzer", licenseCheck: "15.05.2025", assignedVehicles: [] },
        { id: 3, name: "Admin User", role: "Admin", licenseCheck: "—", assignedVehicles: [] },
      ]);
    } finally {
      setLoading(false);
    }
  };



  const handleAssignUser = (user: UserAssignment) => {
    setSelectedUser(user);
    setShowAssignModal(true);
  };

  const handleAssignVehicle = async (vehicle: AvailableVehicle) => {
    if (!selectedUser) return;
    
    try {
      setAssigningVehicle(vehicle);
      
      // Echte API-Zuweisung
      await baserowApi.assignVehicleToUser(vehicle.id, selectedUser.id);
      
      // Aktualisiere lokalen State
      const updatedUsers = users.map(user => {
        if (user.id === selectedUser.id) {
          return {
            ...user,
            assignedVehicles: [...user.assignedVehicles, {
              licensePlate: vehicle.licensePlate,
              model: vehicle.model,
              taxation: vehicle.taxation
            }]
          };
        }
        return user;
      });
      
      setUsers(updatedUsers);
      
      // Entferne das Fahrzeug aus der verfügbaren Liste
      setAvailableVehicles(prev => prev.filter(v => v.id !== vehicle.id));
      
      // Schließe Modal
      setShowAssignModal(false);
      setSelectedUser(null);
      setAssigningVehicle(null);
      
    } catch (error) {
      console.error('Fehler beim Zuweisen des Fahrzeugs:', error);
      setError('Fehler beim Zuweisen des Fahrzeugs. Bitte versuchen Sie es erneut.');
    } finally {
      setAssigningVehicle(null);
    }
  };

  const handleRemoveAssignment = async (user: UserAssignment, vehicleIndex: number) => {
    const vehicle = user.assignedVehicles[vehicleIndex];
    
    try {
      // Finde das Fahrzeug in der Baserow-Daten
      const response = await baserowApi.getVehicles();
      const vehicles = response.results;
      const targetVehicle = vehicles.find((v: CarpoolVehicle) => 
        v.license_plate === vehicle.licensePlate || v.model === vehicle.model
      );
      
      if (targetVehicle) {
        // Entferne die Zuweisung in Baserow
        await baserowApi.removeVehicleAssignment(targetVehicle.id);
        
        // Aktualisiere lokalen State
        const updatedUsers = users.map(u => {
          if (u.id === user.id) {
            return {
              ...u,
              assignedVehicles: u.assignedVehicles.filter((_, index) => index !== vehicleIndex)
            };
          }
          return u;
        });
        
        setUsers(updatedUsers);
        
        // Füge das Fahrzeug zur verfügbaren Liste hinzu
        setAvailableVehicles(prev => [...prev, {
          id: targetVehicle.id,
          licensePlate: vehicle.licensePlate,
          model: vehicle.model,
          taxation: vehicle.taxation,
          manufacturer: typeof targetVehicle.manufacturer === 'string' 
            ? targetVehicle.manufacturer 
            : targetVehicle.manufacturer?.value || 'Unbekannt'
        }]);
      }
    } catch (error) {
      console.error('Fehler beim Entfernen der Zuweisung:', error);
      setError('Fehler beim Entfernen der Zuweisung. Bitte versuchen Sie es erneut.');
    }
  };

  const closeModal = () => {
    setShowAssignModal(false);
    setSelectedUser(null);
    setAssigningVehicle(null);
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Lade Benutzerzuordnungen...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white min-h-screen">
      <div className="mb-4">
        <div className="grid grid-cols-3 items-center">
          <div className="justify-self-start">
            <a href="/" className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              <span>Zurück zum Hub</span>
            </a>
          </div>
          <div className="justify-self-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Fahrer- und Nutzerzuordnung</h2>
          </div>
          <div className="justify-self-end"></div>
        </div>
      </div>

      
      <div className="mb-4">
        <div className="text-sm text-gray-500 dark:text-gray-400 border-l-4 border-yellow-400 pl-2">
          <strong>Notiz:</strong> Die Dokumentation der Zuweisungs- und Führerscheinkontrolledaten ist aus arbeitsrechtlichen Gründen und zur Halterhaftung verpflichtend.
        </div>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          <strong>Fehler:</strong> {error}
        </div>
      )}
      
      {/* Modernes Kachel-Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {users.map(user => (
          <div 
            key={user.id} 
            className="relative overflow-hidden bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-200"
          >
            {/* Status-Indikator */}
            <div className={`absolute top-0 left-0 w-full h-1 ${
              user.isPoolCar ? 'bg-gray-400' : 'bg-[#add015]'
            }`}></div>
            
            <div className="p-6">
              {/* Header mit Avatar */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-4">
                  {user.isPoolCar ? (
                    <div className="p-3 bg-gray-500 rounded-xl">
                      <Car className="w-6 h-6 text-white" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-[#add015] rounded-xl flex items-center justify-center">
                      <span className="text-black font-bold text-lg">
                        {user.avatarInitials || user.name.substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg leading-tight">
                      {user.name}
                    </h3>
                    <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {user.role}
                    </div>
                  </div>
                </div>
                
                {/* Führerschein-Status */}
                {!user.isPoolCar && (
                  <div className="text-right">
                    <div className="bg-white/70 rounded-lg p-2 border border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">
                        Führerschein bis
                      </p>
                      <p className="text-sm font-bold text-gray-900">
                        {user.licenseCheck}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Fahrzeugliste */}
              <div className="mb-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Car className="w-4 h-4 text-gray-500" />
                  <h4 className="text-sm font-semibold text-gray-700">
                    Zugewiesene Fahrzeuge
                  </h4>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    user.assignedVehicles.length > 0 
                      ? 'bg-[#add015] bg-opacity-20 text-[#add015]' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {user.assignedVehicles.length}
                  </span>
                </div>
                
                {user.assignedVehicles.length > 0 ? (
                  <div className="space-y-2">
                    {user.assignedVehicles.map((vehicle, index) => (
                      <div key={`${user.id}-vehicle-${index}`} className="bg-white/70 rounded-xl p-3 border border-gray-200 hover:border-gray-300 transition-colors">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                              <Car className="w-4 h-4 text-gray-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-sm">
                                {vehicle.licensePlate}
                              </p>
                              <p className="text-xs text-gray-500">
                                {vehicle.model}
                              </p>
                            </div>
                          </div>
                          {!user.isPoolCar && (
                            <button
                              onClick={() => handleRemoveAssignment(user, index)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                              title="Zuweisung entfernen"
                            >
                              <Unlink className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-4 text-center border-2 border-dashed border-gray-200">
                    <Car className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      Keine Fahrzeuge zugeordnet
                    </p>
                  </div>
                )}
              </div>

              {/* Aktionen */}
              <div className="pt-4 border-t border-gray-200">
                {!user.isPoolCar ? (
                  <div className="grid grid-cols-1 gap-2">
                    <button 
                      onClick={() => handleAssignUser(user)}
                      className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-[#add015] text-black rounded-xl hover:bg-[#b8e35a] transition-all duration-200 font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Fahrzeug zuweisen</span>
                    </button>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <button 
                        onClick={() => {
                          setSelectedUserForLicenseCheck(user);
                          setShowLicenseCheckModal(true);
                        }}
                        className="flex items-center justify-center space-x-1 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Kontrolle</span>
                      </button>
                      
                      <button 
                        onClick={() => {
                          setSelectedUserForLicenseHistory(user);
                          setShowLicenseHistoryModal(true);
                        }}
                        className="flex items-center justify-center space-x-1 px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                      >
                        <History className="w-4 h-4" />
                        <span>Historie</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <div className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                      <Car className="w-4 h-4 mr-1" />
                      Verfügbares Poolfahrzeug
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {users.length === 0 && !loading && (
        <div className="text-center py-16">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-auto border border-gray-200">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Keine Benutzer gefunden
            </h3>
            <p className="text-gray-500">
              Es wurden noch keine Benutzerzuordnungen erstellt.
            </p>
          </div>
        </div>
      )}

      {/* Zuweisungs-Modal */}
      {showAssignModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-3xl w-full mx-4 max-h-[85vh] overflow-y-auto border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-[#add015] bg-opacity-20 rounded-full">
                  <UserPlus className="w-6 h-6 text-[#add015]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Fahrzeug zuweisen
                  </h3>
                  <p className="text-gray-600">
                    für <strong>{selectedUser.name}</strong>
                  </p>
                </div>
              </div>
              <button 
                onClick={closeModal}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {availableVehicles.length > 0 ? (
              <div>
                <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
                  <p className="text-gray-800 font-medium mb-1">
                    Verfügbare Fahrzeuge ({availableVehicles.length})
                  </p>
                  <p className="text-gray-600 text-sm">
                    Klicken Sie auf ein Fahrzeug, um es zuzuweisen
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availableVehicles.map(vehicle => (
                    <div 
                      key={vehicle.id}
                      className="relative group border-2 border-gray-200 rounded-2xl p-4 hover:border-[#add015] hover:shadow-lg cursor-pointer transition-all duration-200 bg-white"
                      onClick={() => handleAssignVehicle(vehicle)}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gray-500 rounded-xl flex items-center justify-center">
                          <Car className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-900 text-lg">
                            {vehicle.licensePlate}
                          </p>
                          <p className="text-gray-600 text-sm">
                            {vehicle.manufacturer} {vehicle.model}
                          </p>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <UserPlus className="w-5 h-5 text-[#add015]" />
                        </div>
                      </div>
                      
                      {assigningVehicle?.id === vehicle.id && (
                        <div className="absolute inset-0 bg-white/90 rounded-2xl flex items-center justify-center">
                          <div className="flex items-center space-x-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#add015]"></div>
                            <span className="text-[#add015] font-medium">Zuweisen...</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="bg-gray-50 rounded-2xl p-8 max-w-sm mx-auto">
                  <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    Keine Fahrzeuge verfügbar
                  </h4>
                  <p className="text-gray-500">
                    Alle Fahrzeuge sind bereits zugewiesen.
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex justify-end mt-8 pt-6 border-t border-gray-200">
              <button 
                onClick={closeModal}
                className="px-8 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Führerscheinkontrolle-Modal */}
      {showLicenseCheckModal && selectedUserForLicenseCheck && (
        <LicenseCheckModal
          open={showLicenseCheckModal}
          user={selectedUserForLicenseCheck}
          onClose={() => setShowLicenseCheckModal(false)}
          onLicenseCheck={(userId, checkData) => {
            // Berechne das Ablaufdatum (Kontrolldatum + 6 Monate)
            const expiryDate = calculateExpiryDate(checkData.checkDate);
            const formattedExpiryDate = formatDateForDisplay(expiryDate);
            
            const updatedUsers = users.map(user => {
              if (user.id === userId) {
                return {
                  ...user,
                  licenseCheck: formattedExpiryDate, // Zeige das berechnete Ablaufdatum
                };
              }
              return user;
            });
            setUsers(updatedUsers);
            setSelectedUserForLicenseCheck(null);
          }}
        />
      )}

      {/* Führerschein-Historie-Modal */}
      {showLicenseHistoryModal && selectedUserForLicenseHistory && (
        <LicenseHistoryModal
          open={showLicenseHistoryModal}
          user={selectedUserForLicenseHistory}
          onClose={() => setShowLicenseHistoryModal(false)}
        />
      )}


    </div>
  );
}

 